import { useCallback, useEffect, useRef, useState } from "react";
import { usePlatform } from "../provider";

const PREFIX = "luckymenu.draft.";

/**
 * A form draft that survives a reload or an app restart (UX rule 4), kept in
 * the platform's key-value storage under `key`. `initial` is the draft of
 * the saved row (memoise it); `baseOf` gives what identifies it, so a stored
 * draft is restored only while the saved row is still the one it was
 * started from. Null `draft` while loading.
 */
export function useStoredDraft<T>(key: string, initial: T | null, baseOf: (draft: T) => string) {
	const { storage } = usePlatform();
	const [state, setState] = useState<{ key: string; draft: T; restored: boolean } | null>(null);
	const base = initial === null ? null : baseOf(initial);
	const written = useRef<string | null>(null);

	useEffect(() => {
		if (initial === null || base === null || state?.key === key) return;
		let cancelled = false;
		const done = (draft: T | null) => {
			if (cancelled) return;
			written.current = null;
			setState({ key, draft: draft ?? initial, restored: draft !== null });
		};
		storage.getItem(PREFIX + key).then(
			(raw) => {
				try {
					const saved = raw ? (JSON.parse(raw) as { base: string; draft: T }) : null;
					done(saved?.base === base ? saved.draft : null);
				} catch {
					done(null);
				}
			},
			() => done(null),
		);
		return () => {
			cancelled = true;
		};
	}, [key, initial, base, state?.key, storage]);

	// Every change is written at once, so nothing typed is lost to a crash.
	useEffect(() => {
		if (!state || state.key !== key || base === null) return;
		const unchanged = baseOf(state.draft) === base;
		const value = unchanged ? null : JSON.stringify({ base, draft: state.draft });
		if (value === written.current) return;
		written.current = value;
		if (value === null) void storage.removeItem(PREFIX + key).catch(() => {});
		else void storage.setItem(PREFIX + key, value).catch(() => {});
	}, [state, key, base, baseOf, storage]);

	const update = useCallback((change: (draft: T) => T) => {
		setState((s) => (s ? { ...s, draft: change(s.draft) } : s));
	}, []);

	/** Starts again from `next` (after a save) or from the saved row, forgetting the stored draft. */
	const reset = useCallback(
		(next?: T) => {
			const draft = next ?? initial;
			if (draft === null) return;
			setState({ key, draft, restored: false });
		},
		[key, initial],
	);

	/** Removes the stored draft, such as after the row was created or deleted. */
	const forget = useCallback(() => {
		written.current = null;
		void storage.removeItem(PREFIX + key).catch(() => {});
	}, [key, storage]);

	const ready = state !== null && state.key === key;
	return {
		draft: ready ? state.draft : null,
		/** The draft came back from storage: say so, and offer to discard it. */
		restored: ready ? state.restored : false,
		/** The draft differs from the saved row. */
		dirty: ready && base !== null && baseOf(state.draft) !== base,
		update,
		reset,
		forget,
	};
}
